import * as THREE from 'three';
import type { GameContext } from '../core/context';
import type { System } from '../core/system';

/**
 * F3: performance/entity overlay. F4: physics collider wireframes.
 * Reads perf numbers through the `getPerf` / `snapshot` hooks so it shows exactly what tests see.
 */
export function createDebugOverlaySystem(): System {
  let ctx: GameContext;
  let el: HTMLDivElement | null = null;
  let visible = false;
  let physicsVisible = false;
  let acc = 0;
  let staticLines: THREE.LineSegments | null = null;
  let bodyLines: THREE.LineSegments | null = null;
  const lineMat = new THREE.LineBasicMaterial({ color: 0x40ff90, depthTest: false, transparent: true, opacity: 0.8 });
  const bodyMat = new THREE.LineBasicMaterial({ color: 0xff5050, depthTest: false });

  function buildStaticLines(): THREE.LineSegments {
    const pts: number[] = [];
    for (const s of ctx.physics.statics) {
      const g = s.geom;
      if (g.isCircle) {
        const n = 16;
        for (let i = 0; i < n; i++) {
          const a0 = (i / n) * Math.PI * 2;
          const a1 = ((i + 1) / n) * Math.PI * 2;
          pts.push(g.center.x + Math.cos(a0) * g.radius, 0.3, g.center.z + Math.sin(a0) * g.radius);
          pts.push(g.center.x + Math.cos(a1) * g.radius, 0.3, g.center.z + Math.sin(a1) * g.radius);
        }
      } else {
        for (let i = 0; i < 4; i++) {
          const a = g.verts[i]!;
          const b = g.verts[(i + 1) % 4]!;
          pts.push(a.x, 0.3, a.z, b.x, 0.3, b.z);
        }
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
    const lines = new THREE.LineSegments(geo, lineMat);
    lines.renderOrder = 999;
    lines.frustumCulled = false;
    return lines;
  }

  function updateBodyLines(): void {
    const pts: number[] = [];
    for (const b of ctx.physics.bodies) {
      if (!b.enabled) continue;
      const g = b.geom;
      if (g.isCircle) {
        for (let i = 0; i < 8; i++) {
          const a0 = (i / 8) * Math.PI * 2;
          const a1 = ((i + 1) / 8) * Math.PI * 2;
          pts.push(b.pos.x + Math.cos(a0) * g.radius, 0.35, b.pos.z + Math.sin(a0) * g.radius);
          pts.push(b.pos.x + Math.cos(a1) * g.radius, 0.35, b.pos.z + Math.sin(a1) * g.radius);
        }
      } else {
        for (let i = 0; i < 4; i++) {
          const a = g.verts[i]!;
          const c = g.verts[(i + 1) % 4]!;
          pts.push(a.x, 0.35, a.z, c.x, 0.35, c.z);
        }
        const f = b.forward();
        pts.push(b.pos.x, 0.35, b.pos.z, b.pos.x + f.x * 3, 0.35, b.pos.z + f.z * 3);
      }
    }
    if (!bodyLines) {
      bodyLines = new THREE.LineSegments(new THREE.BufferGeometry(), bodyMat);
      bodyLines.renderOrder = 1000;
      bodyLines.frustumCulled = false;
      ctx.scene.add(bodyLines);
    }
    bodyLines.geometry.dispose();
    bodyLines.geometry = new THREE.BufferGeometry();
    bodyLines.geometry.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
  }

  function setPhysicsVisible(v: boolean): void {
    physicsVisible = v;
    if (v && !staticLines) {
      staticLines = buildStaticLines();
      ctx.scene.add(staticLines);
    }
    if (staticLines) staticLines.visible = v;
    if (bodyLines) bodyLines.visible = v;
  }

  function render(): void {
    if (!el) return;
    const p = ctx.hooks.call('getPerf') as Record<string, number>;
    const snap = ctx.hooks.collectSnapshot();
    const player = snap.player as { mode?: string; x?: number; z?: number; speed?: number } | undefined;
    const lines = [
      `<b>BOROUGH HEAT</b> debug  <span style="opacity:.6">F3 hide · F4 colliders</span>`,
      `fps ${p.fps!.toFixed(0)}  frame ${p.frameMs!.toFixed(1)}ms (max ${p.frameMsMax!.toFixed(1)})`,
      `sim ${p.simMs!.toFixed(2)}ms  physics ${p.physicsMs!.toFixed(2)}ms  update ${p.updateMs!.toFixed(2)}ms  steps ${p.stepsLastFrame}`,
      `draw calls ${p.drawCalls}  tris ${(p.triangles! / 1000).toFixed(1)}k  geo ${p.geometries}  tex ${p.textures}`,
      `bodies ${p.bodies} (awake ${p.awakeBodies})  statics ${ctx.physics.stats.statics}`,
      `heat ${ctx.state.heat}  money £${ctx.state.money}  mode ${ctx.state.mode}`,
    ];
    if (player) lines.push(`player ${player.mode} @ ${player.x?.toFixed(1)}, ${player.z?.toFixed(1)}  ${((player.speed ?? 0) * 2.237).toFixed(0)} mph`);
    for (const [k, v] of Object.entries(snap)) {
      if (k === 'player' || k === 'physics' || k === 'world') continue;
      const o = v as Record<string, unknown>;
      const brief = Object.entries(o)
        .filter(([, x]) => typeof x === 'number' || typeof x === 'string' || typeof x === 'boolean')
        .map(([kk, x]) => `${kk} ${typeof x === 'number' ? +x.toFixed(2) : String(x)}`)
        .join('  ');
      if (brief) lines.push(`${k}: ${brief}`);
    }
    el.innerHTML = lines.join('<br>');
  }

  return {
    id: 'debug-overlay',
    order: 795,
    init(c) {
      ctx = c;
      if (!c.uiRoot) return;
      el = document.createElement('div');
      el.id = 'debug-overlay';
      el.style.cssText =
        'position:absolute;top:8px;left:8px;padding:8px 10px;background:rgba(10,10,12,.78);color:#d8f5d0;' +
        'font:12px/1.45 ui-monospace,Consolas,monospace;border-left:3px solid #c8a24a;pointer-events:none;' +
        'z-index:1000;display:none;white-space:pre;';
      c.uiRoot.appendChild(el);
      if (new URLSearchParams(location.search).has('debug')) visible = true;
    },
    update(dt) {
      const input = ctx.input;
      if (input.framePressed('debug')) visible = !visible;
      if (input.framePressed('debugPhysics')) setPhysicsVisible(!physicsVisible);
      if (physicsVisible) updateBodyLines();
      if (!el) return;
      el.style.display = visible ? 'block' : 'none';
      if (!visible) return;
      acc += dt;
      if (acc >= 0.25) {
        acc = 0;
        render();
      }
    },
    dispose() {
      el?.remove();
      if (staticLines) {
        ctx.scene.remove(staticLines);
        staticLines.geometry.dispose();
      }
      if (bodyLines) {
        ctx.scene.remove(bodyLines);
        bodyLines.geometry.dispose();
      }
      lineMat.dispose();
      bodyMat.dispose();
    },
  };
}
